import { APP_INITIALIZER, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { environment } from "../environments/environment";
import { MatButtonModule } from "@angular/material/button";
import { LandingPageComponent } from "./pages/landing-page/landing-page.component";
import { FormPageComponent } from "./pages/form-page/form-page.component";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatDialogModule } from "@angular/material/dialog";
import { RoundedButtonComponent } from "./components/rounded-button/rounded-button.component";
import { ProgressBarComponent } from "./components/progress-bar/progress-bar.component";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatFormField, MatFormFieldModule } from "@angular/material/form-field";
import { DynamicInputsModule } from "./components/dynamic-inputs/dynamic-inputs.module";
import { BaseLayoutComponent } from "./components/base-layout/base-layout.component";
import { LoadingPageComponent } from "./pages/loading-page/loading-page.component";
import { LoadingComponent } from "./components/loading/loading.component";
import { BiometryPageComponent } from "./pages/biometry-page/biometry-page.component";
import { SendingPageComponent } from "./pages/sending-page/sending-page.component";
import { CapturePageComponent } from "./pages/capture-page/capture-page.component";
import { ApplicationSentPageComponent } from "./pages/application-sent-page/application-sent-page.component";
import { ReviewFormPageComponent } from "./pages/review-form-page/review-form-page.component";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import { ProgressComponent } from "./components/progress-bar/progress/progress.component";
import { NgxIndexedDBModule } from "ngx-indexed-db";
import { dbConfig } from "./db.config";
import { ImageCaptureInputModule } from "./components/image-capture-input.module";
import { DependenciesGroupComponent } from "./components/dependencies-group/dependencies-group.component";
import { ProductPageComponent } from "./pages/product-page/product-page.component";
import { SafeHtmlPipe } from "./utils/safe-html-pipe";
import { ConfigService } from "./services/config.service";

import { TimeLineStepComponent } from "./components/time-line-step/time-line-step.component";

import {
  RecaptchaModule,
  RecaptchaFormsModule,
  RECAPTCHA_V3_SITE_KEY,
  RecaptchaV3Module,
} from "ng-recaptcha";
import { NgSelectModule } from "@ng-select/ng-select";
import { DialogComponent } from "./components/dialog/dialog.component";
import { WelcomePageComponent } from "./pages/welcome-page/welcome-page.component";
import { WaitingPageComponent } from "./pages/waiting-page/waiting-page.component";
import { ConfirmedPageComponent } from "./pages/confirmed-page/confirmed-page.component";
import { DialogHtmlComponent } from "./components/dialog-html/dialog-html.component";
import { HttpSmilegoInterceptor } from "./utils/http-smilego-interceptor";
import { ConfirmationDialogComponent } from "./components/confirmation-dialog/confirmation-dialog.component";
import { GeolocationLoadingOverlayComponent } from "./components/geolocation-loading-overlay/geolocation-loading-overlay.component";
import { ReanalysisPageComponent } from "./pages/reanalisys-page/reanalysis-page.component";
import { NgxTurnstileComponent } from "./components/turnstile/ngx-turnstile.component";
import { SmsVerificationPageComponent } from "./pages/sms-verification-page/sms-verification-page.component";
import { SessionStorageService } from "./services/session-storage.service";
// import { NgxTurnstileModule } from "ngx-turnstile";

import { datadogLogs } from '@datadog/browser-logs';
// @ts-ignore
import { default as pkg } from '../../package.json';
import { ensureFreshBuild } from "./utils/app-version";

if (environment.production) {
  datadogLogs.init({
    clientToken: 'pubf277e965a52f6c0a99dae251d22702d2',
    site: 'datadoghq.com',
    forwardErrorsToLogs: true,
    forwardConsoleLogs: environment.env == 'prod' ? ['warn', 'error'] : 'all',
    service: 'web-v2',
    env: environment.env ?? 'undefined',
    sampleRate: 100,
    version: pkg.version
  });
}
function appLoadFactory(config: ConfigService) {
  return () =>
    ensureFreshBuild().then(() =>
      config.loadConfig().then(() => console.log(`config resolved`))
    );
}

function storageLoadFactory(storage: SessionStorageService) {
  return () => ensureFreshBuild().then(() => storage.initialize());
}

@NgModule({
  declarations: [
    AppComponent,
    LandingPageComponent,
    FormPageComponent,
    RoundedButtonComponent,
    ProgressBarComponent,
    BaseLayoutComponent,
    LoadingPageComponent,
    LoadingComponent,
    BiometryPageComponent,
    SendingPageComponent,
    CapturePageComponent,
    ApplicationSentPageComponent,
    ReviewFormPageComponent,
    ProgressComponent,
    DependenciesGroupComponent,
    ProductPageComponent,
    SafeHtmlPipe,
    TimeLineStepComponent,
    DialogComponent,
    DialogHtmlComponent,
    ConfirmationDialogComponent,
    GeolocationLoadingOverlayComponent,
    WelcomePageComponent,
    WaitingPageComponent,
    ConfirmedPageComponent,
    ReanalysisPageComponent,
    NgxTurnstileComponent,
    SmsVerificationPageComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    MatButtonModule,
    MatProgressBarModule,
    MatCheckboxModule,
    DynamicInputsModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    HttpClientModule,
    NgxIndexedDBModule.forRoot(dbConfig),
    BrowserAnimationsModule,
    ReactiveFormsModule,
    ImageCaptureInputModule,
    NgSelectModule,
    FormsModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    RecaptchaV3Module,
    MatDialogModule,
    MatFormFieldModule,
    // NgxTurnstileModule,
  ],
  providers: [
    // Lazy load reCAPTCHA v3 site key https://github.com/DethAriel/ng-recaptcha/issues/165
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useFactory: (config: ConfigService) => {
        return config.recaptchaSiteKeyV3;
      },
      deps: [ConfigService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: storageLoadFactory,
      deps: [SessionStorageService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appLoadFactory,
      deps: [ConfigService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpSmilegoInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
